import { Platform, StyleSheet } from 'react-native'
import type { ThemeMode } from '../../../theme/ThemeContext'
import type { Theme } from '../../../theme/getTheme'

/**
 * @param themeMode — featured Pro price is white on dark cards only; light/normal keep purple on white cards.
 */
export function getPricingScreenStyles(theme: Theme, themeMode: ThemeMode = 'light') {
  const featuredPriceColor = themeMode === 'dark' ? theme.white : theme.primary

  return StyleSheet.create({
    screenRoot: {
      flex: 1,
      backgroundColor: theme.background.primary,
    },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.huge,
      paddingTop: theme.spacing.md,
      paddingBottom: theme.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: theme.gray[200],
      maxWidth: 1200,
      width: '100%',
      alignSelf: 'center',
    },
    backButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      minHeight: theme.touchTargets.minHeight,
      paddingVertical: theme.spacing.sm,
      paddingRight: theme.spacing.lg,
    },
    backButtonPressed: {
      opacity: 0.75,
    },
    backButtonHoverWeb: {
      opacity: 0.9,
    },
    backLabel: {
      ...theme.typography.label,
      color: theme.primary,
      fontWeight: '700',
    },
    scroll: {
      flex: 1,
      backgroundColor: theme.background.primary,
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    scrollContent: {
      paddingHorizontal: theme.spacing.huge,
      paddingTop: theme.spacing.lg,
      paddingBottom: theme.spacing.xxxl,
      width: '100%',
      maxWidth: 1200,
      alignSelf: 'center',
    },
    pageTitle: {
      ...theme.typography.h1,
      color: theme.gray[900],
      marginBottom: theme.spacing.sm,
      fontWeight: '800',
    },
    pageSubtitle: {
      ...theme.typography.body,
      color: theme.gray[600],
      marginBottom: theme.spacing.xxl,
      maxWidth: 620,
    },

    cardsGrid: {
      flexDirection: 'column',
      gap: theme.spacing.lg,
    },
    cardsRow1: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: theme.spacing.lg,
    },
    cardsRow2: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: theme.spacing.lg,
      ...(Platform.OS === 'web' && ({
        maxWidth: 760,
        width: '100%',
        alignSelf: 'center',
      } as object)),
    },

    card: {
      flexDirection: 'column',
      alignSelf: 'stretch',
      flexGrow: 1,
      flexBasis: 0,
      minWidth: 200,
      borderRadius: theme.borderRadius.lg,
      borderWidth: 1,
      borderColor: theme.gray[200],
      backgroundColor: theme.background.card,
      padding: theme.spacing.xl,
      gap: 14,
      ...(Platform.OS === 'web' && {
        boxShadow: theme.shadows.card,
        overflow: 'visible',
      } as object),
    },
    cardHighlighted: {
      borderColor: theme.primary,
      borderWidth: 2,
    },
    cardCurrent: {
      backgroundColor: theme.gray[50],
    },

    cardTopRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: theme.spacing.md,
    },
    cardIconWrap: {
      width: 52,
      height: 52,
      borderRadius: 16,
      backgroundColor: 'rgba(107, 70, 193, 0.1)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    cardIconWrapFeatured: {
      backgroundColor: theme.primary,
    },
    badgeTopSlot: {
      flex: 1,
      alignItems: 'flex-end',
      justifyContent: 'center',
      minHeight: 52,
    },
    badgeComingSoonPill: {
      alignSelf: 'flex-end',
      backgroundColor: theme.accent,
      paddingVertical: 6,
      paddingHorizontal: 12,
      borderRadius: theme.borderRadius.full,
    },
    /** Dark text on `theme.accent` yellow — purple (`primaryDark`) fails contrast on gold. */
    badgeComingSoonPillText: {
      fontSize: 12,
      fontWeight: '800',
      color: '#1A202C',
    },
    cardName: {
      fontSize: 22,
      fontWeight: '800',
      letterSpacing: -0.45,
      color: theme.gray[900],
      marginBottom: theme.spacing.sm,
    },
    cardPriceRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      gap: 4,
    },
    cardPrice: {
      fontSize: 28,
      fontWeight: '800',
      letterSpacing: -0.8,
      color: theme.gray[900],
    },
    /** Featured Pro tier: white in dark mode; purple on light/normal (white card). */
    cardPriceHighlighted: {
      color: featuredPriceColor,
    },
    cardPriceSub: {
      fontSize: 13,
      color: theme.gray[500],
    },
    cardPriceSubHighlighted: {
      fontSize: 15,
      fontWeight: '700',
      color: featuredPriceColor,
    },
    cardTagline: {
      ...theme.typography.bodySmall,
      color: theme.gray[600],
    },

    badge: {
      alignSelf: 'flex-start',
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 6,
    },
    badgeCurrent: {
      backgroundColor: theme.primary,
    },
    badgeComingSoon: {
      backgroundColor: theme.gray[200],
    },
    badgeText: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.white,
    },
    badgeComingSoonText: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.gray[600],
    },
    /** Cancel at period end — still Pro until date; distinct from active renewal. */
    badgeEndsScheduled: {
      alignSelf: 'flex-start',
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 6,
      backgroundColor: theme.warning,
    },
    badgeEndsScheduledText: {
      fontSize: 12,
      fontWeight: '700',
      color: '#1A202C',
    },

    featureList: {
      flex: 1,
      gap: 8,
    },
    featureRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    featureIcon: {
      width: 18,
      alignItems: 'center',
    },
    featureLabel: {
      fontSize: 14,
      color: theme.gray[700],
      flexShrink: 1,
    },
    featureLabelExcluded: {
      color: theme.gray[400],
    },

    cta: {
      marginTop: 'auto',
      minHeight: theme.touchTargets.minHeight,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.primary,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
    },
    ctaPressed: {
      opacity: 0.88,
    },
    ctaHoverWeb: {
      opacity: 0.94,
    },
    ctaDisabled: {
      opacity: 0.45,
    },
    ctaText: {
      ...theme.typography.label,
      color: theme.white,
      fontWeight: '700',
    },
    ctaOutline: {
      marginTop: 'auto',
      minHeight: theme.touchTargets.minHeight,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.background.card,
      borderWidth: 2,
      borderColor: theme.primary,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
    },
    ctaOutlineText: {
      ...theme.typography.label,
      color: theme.primary,
      fontWeight: '700',
    },

    orgPlanDisclosure: {
      ...theme.typography.caption,
      color: theme.gray[500],
      lineHeight: 18,
    },
    footnote: {
      ...theme.typography.caption,
      color: theme.gray[500],
      marginTop: theme.spacing.xxxl,
      maxWidth: 640,
    },
    footnoteSecondary: {
      ...theme.typography.caption,
      color: theme.gray[500],
      marginTop: theme.spacing.md,
      maxWidth: 640,
    },
  })
}
